import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    IonSearchbar,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonChip,
    IonBadge,
    IonModal,
    IonItem,
    IonInput,
    IonTextarea,
    IonSelect,
    IonSelectOption,
    IonToggle,
    IonToast,
    IonMenuButton,
    useIonRouter
} from '@ionic/react';
import GlobalHelp from '../components/GlobalHelp';
import {
    peopleOutline,
    personAddOutline,
    banOutline,
    checkmarkCircleOutline,
    warningOutline,
    ellipsisVerticalOutline,
    createOutline,
    trashOutline,
    mailOutline,
    calendarOutline,
    shieldCheckmarkOutline,
    eyeOutline,
    filterOutline,
    checkmarkCircle,
    closeCircle,
    saveOutline,
    closeOutline,
    chevronBackOutline,
    chevronForwardOutline,
    personOutline
} from 'ionicons/icons';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import Loader from '../components/Loader';
import './View.css';

const AdminUsers = () => {
    const router = useIonRouter();
    const modal = useRef(null);

    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [currentAdminRole, setCurrentAdminRole] = useState(null);
    const [currentAdminOrgId, setCurrentAdminOrgId] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [selectedSegment, setSelectedSegment] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        const roleId = String(sessionStorage.getItem('role_id') || localStorage.getItem('roleId') || '');
        const allRoles = [
            { id: '1', name: 'Super Admin' },
            { id: '2', name: 'Organization Admin' },
            { id: '3', name: 'Moderator' },
            { id: '4', name: 'Regular User' },
            { id: '5', name: 'Viewer' }
        ];

        // Filter roles: Org Admins (2) cannot manage Super Admins (1) or create other Org Admins (2)
        if (roleId === '2') {
            setRoles(allRoles.filter(r => parseInt(r.id) > 2));
        } else {
            setRoles(allRoles);
        }
    }, []);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        suspended: 0,
        newThisMonth: 0
    });
    const [toast, setToast] = useState({ show: false, message: '', color: 'success' });
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    useEffect(() => {
        // Check permissions
        const roleId = sessionStorage.getItem('role_id') || localStorage.getItem('roleId');
        const orgId = sessionStorage.getItem('organization_id') || localStorage.getItem('organizationId');
        const userId = sessionStorage.getItem('user_id') || localStorage.getItem('userId');

        setCurrentAdminRole(roleId);
        setCurrentAdminOrgId(orgId);
        setCurrentUserId(userId);

        // Allow Role 1 (Super Admin) OR Role 2 (Org Admin)
        if (!sessionStorage.getItem('user_id') || (roleId !== '2' && roleId !== '1')) {
            window.location.replace('/page/login');
            return;
        }

        fetchUsers(roleId, orgId);
        fetchOrganizations();
        setCurrentPage(1);
    }, [selectedSegment, searchText]); // Only re-run when filters change

    const fetchOrganizations = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.get(`${API_CONFIG.MANAGE_ORGANIZATIONS}?user_id=${userId}`);
            if (response.data && response.data.success && Array.isArray(response.data.organizations)) {
                setOrganizations(response.data.organizations);
            }
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    // Modified to accept params directly or from state
    const fetchUsers = async (roleIdOverride, orgIdOverride) => {
        setLoading(true);
        const safetyTimeout = setTimeout(() => {
            setLoading(false);
        }, 12000);

        try {
            // Get credentials from args or storage
            const userId = localStorage.getItem('userId');
            // If called from useEffect, we might use overrides. 
            // BUT this function is also called by refresh/delete etc without args.
            // So we need safe fallbacks.

            const currentRoleId = roleIdOverride || sessionStorage.getItem('role_id');
            const currentOrgId = orgIdOverride || localStorage.getItem('organizationId');

            if (!userId) {
                throw new Error('User not logged in. Please log in to continue.');
            }

            const params = new URLSearchParams({
                user_id: userId,
                filter: selectedSegment,
                search: searchText
            });

            // If NOT Super Admin (1), force organization filter
            if (currentRoleId !== '1') {
                if (currentOrgId && currentOrgId !== 'null') {
                    params.append('organization_id', currentOrgId);
                } else {
                    // Safety: Org Admin without Org ID shouldn't see anything? 
                    // Or maybe they see nothing. Let's assume standard behavior.
                }
            } else {
                // If Super Admin, do NOT append organization_id implies "All Orgs"
                // effectively assuming backend handles "no org_id means all" for super admin context
            }

            console.log('API Request URL:', `${API_CONFIG.MANAGE_USERS}?${params.toString()}`);

            const response = await axios.get(
                `${API_CONFIG.MANAGE_USERS}?${params.toString()}`,
                { timeout: 8000 }
            );

            console.log('API Response:', response.data);

            if (response.data.success) {
                // Transform data to match UI expectations
                const transformedUsers = response.data.users.map(user => {
                    const nameParts = (user.name || '').split(' ');
                    return {
                        ...user, // Preserve original properties
                        id: user.id,
                        username: user.email.split('@')[0],
                        email: user.email,
                        fullName: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                        firstName: user.first_name || nameParts[0] || '',
                        lastName: user.last_name || nameParts.slice(1).join(' ') || '',
                    status: user.is_active === 1 || user.is_active === '1' ? 'active' : 'suspended',
                    role: getRoleName(user.role_id),
                    roleId: user.role_id,
                    roleName: user.role_name,
                    phone: user.phone || user.Phone || user.contact_phone || user.phone_number || '',
                    organizationId: user.organization_id,
                    organizationName: user.organization_name,
                    joinDate: user.created_at,
                    lastActive: user.last_login_at || user.created_at,
                        pollsCreated: parseInt(user.polls_created) || 0,
                        votesCount: parseInt(user.votes_count) || 0,
                        avatar: getAvatar(user.role_id)
                    };
                });

                setUsers(transformedUsers);
                setFilteredUsers(transformedUsers);

                // Set stats from backend
                setStats({
                    total: parseInt(response.data.stats.total) || 0,
                    active: parseInt(response.data.stats.active) || 0,
                    suspended: parseInt(response.data.stats.suspended) || 0,
                    newThisMonth: parseInt(response.data.stats.new_this_month) || 0
                });
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to load users. Please try again.';
            // Avoid alert during automated retry/refresh to prevent blocking
            setUsers([]);
            setFilteredUsers([]);
            setStats({ total: 0, active: 0, suspended: 0, newThisMonth: 0 });
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    };

    const getRoleName = (roleId) => {
        switch (parseInt(roleId)) {
            case 1: return 'admin';
            case 2: return 'admin';
            case 3: return 'moderator';
            case 4: return 'user';
            default: return 'user';
        }
    };

    const getAvatar = (roleId) => {
        switch (parseInt(roleId)) {
            case 1: return '👑'; // Super admin
            case 2: return '👩‍💼'; // Org admin
            case 3: return '⚙️'; // Moderator
            default: return '👤'; // Regular user
        }
    };

    // Pagination computed values
    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleUserAction = async (action, user) => {
        switch (action) {
            case 'view':
                setSelectedUser(user);
                setIsModalOpen(true);
                break;
            case 'edit':
                // Open edit modal instead of navigating
                setEditingUser({
                    id: user.id,
                    first_name: user.first_name || user.firstName || '',
                    last_name: user.last_name || user.lastName || '',
                    email: user.email || '',
                    phone: user.phone || user.Phone || user.contact_phone || user.phone_number || '',
                    role_id: String(user.role_id || user.roleId || '4'),
                    organization_id: (user.organization_id || user.organizationId) ? String(user.organization_id || user.organizationId) : '',
                    is_active: user.status === 'active' || user.is_active === '1' || user.is_active === 1
                });
                setIsEditModalOpen(true);
                break;
            case 'suspend':
                await updateUserStatus(user.id, 0, user.fullName);
                break;
            case 'activate':
                await updateUserStatus(user.id, 1, user.fullName);
                break;
            case 'delete':
                if (window.confirm(`Are you sure you want to delete ${user.fullName}?`)) {
                    await deleteUser(user.id);
                }
                break;
            default:
                console.log('Unknown action:', action);
                break;
        }
    };

    const updateUserStatus = async (targetUserId, status, userName) => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.put(
                API_CONFIG.MANAGE_USERS,
                {
                    user_id: userId,
                    id: targetUserId,
                    is_active: status
                }
            );

            if (response.data.success) {
                const action = status === 1 ? 'activated' : 'suspended';
                setToast({
                    show: true,
                    message: `${userName} has been ${action} successfully`,
                    color: status === 1 ? 'success' : 'warning'
                });
                fetchUsers(); // Refresh the list
            } else {
                setToast({ show: true, message: response.data.error || 'Failed to update user status', color: 'danger' });
            }
        } catch (error) {
            console.error('Error updating user status:', error);
            setToast({ show: true, message: 'Failed to update user status', color: 'danger' });
        }
    };

    const deleteUser = async (targetUserId) => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await axios.delete(
                `${API_CONFIG.MANAGE_USERS}?user_id=${userId}&id=${targetUserId}`
            );

            if (response.data.success) {
                setToast({ show: true, message: 'User deleted successfully', color: 'success' });
                fetchUsers(); // Refresh the list
            } else {
                setToast({ show: true, message: response.data.error || 'Failed to delete user', color: 'danger' });
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            setToast({ show: true, message: error.response?.data?.error || 'Failed to delete user', color: 'danger' });
        }
    };

    const handleEditInputChange = (field, value) => {
        setEditingUser(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveEdit = async () => {
        try {
            // Validation
            if (!editingUser.email || !editingUser.first_name || !editingUser.last_name) {
                setToast({ show: true, message: 'Please fill in all required fields', color: 'warning' });
                return;
            }

            setSaving(true);
            const userId = localStorage.getItem('userId');

            const response = await axios.put(
                API_CONFIG.MANAGE_USERS,
                {
                    user_id: userId,
                    id: editingUser.id,
                    first_name: editingUser.first_name,
                    last_name: editingUser.last_name,
                    name: `${editingUser.first_name} ${editingUser.last_name}`,
                    email: editingUser.email,
                    phone: editingUser.phone,
                    role_id: editingUser.role_id,
                    organization_id: editingUser.organization_id,
                    is_active: editingUser.is_active ? 1 : 0
                }
            );

            if (response.data.success) {
                setToast({ show: true, message: 'User updated successfully!', color: 'success' });
                setIsEditModalOpen(false);
                fetchUsers(); // Refresh the list
            } else {
                setToast({ show: true, message: response.data.error || 'Failed to update user', color: 'danger' });
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setToast({ show: true, message: error.response?.data?.error || 'Failed to update user', color: 'danger' });
        } finally {
            setSaving(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'success';
            case 'suspended': return 'danger';
            default: return 'medium';
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return 'danger';
            case 'moderator': return 'warning';
            case 'user': return 'primary';
            default: return 'medium';
        }
    };


    return (
        <IonPage className="view-page admin-indigo-theme">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="custom-back-button" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <div className="title-container">
                            <span className="title-text" style={{ background: 'none', WebkitTextFillColor: '#6366f1', color: '#6366f1' }}>
                                {currentAdminRole === '1' ? 'System User Management' : 'Organization Members'}
                            </span>
                        </div>
                    </IonTitle>
                    <IonButtons slot="end">
                        <GlobalHelp inline={true} />
                        <IonButton fill="clear">
                            <IonIcon icon={personAddOutline} />
                        </IonButton>
                        <IonMenuButton autoHide={false} className="ion-hide-md-up" />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                {loading && <Loader message="Processing User Data..." />}
                <div className="polls-container">

                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Users</p>
                                    <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                                </div>
                                <div className="rounded-full p-3" style={{ backgroundColor: '#6366f115' }}>
                                    <IonIcon icon={peopleOutline} className="text-xl" style={{ color: '#6366f1' }} />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Active</p>
                                    <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                                </div>
                                <div className="bg-green-100 rounded-full p-3">
                                    <IonIcon icon={checkmarkCircleOutline} className="text-xl text-green-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Suspended</p>
                                    <p className="text-2xl font-bold text-red-600">{stats.suspended}</p>
                                </div>
                                <div className="bg-red-100 rounded-full p-3">
                                    <IonIcon icon={banOutline} className="text-xl text-red-600" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">New This Month</p>
                                    <p className="text-2xl font-bold text-indigo-600">{stats.newThisMonth}</p>
                                </div>
                                <div className="bg-indigo-100 rounded-full p-3">
                                    <IonIcon icon={personAddOutline} className="text-xl text-indigo-600" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                        <div className="flex flex-col-reverse md:flex-col gap-4">
                            <div className="flex md:justify-end items-center space-x-4 w-full">
                                <IonSegment 
                                    value={selectedSegment} 
                                    onIonChange={(e) => setSelectedSegment(e.detail.value)}
                                    className="custom-segment-indigo"
                                    style={{ '--color-checked': '#6366f1', '--indicator-color': '#6366f1', '--indicator-color-checked': '#6366f1' }}
                                >
                                    <IonSegmentButton value="all">
                                        <IonLabel>All</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="active">
                                        <IonLabel>Active</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="suspended">
                                        <IonLabel>Suspended</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="admins">
                                        <IonLabel>Admins</IonLabel>
                                    </IonSegmentButton>
                                </IonSegment>
                            </div>
                            <div className="w-full">
                                <IonSearchbar
                                    value={searchText}
                                    onIonInput={(e) => setSearchText(e.detail.value)}
                                    placeholder="Search users by name, email, or username..."
                                    className="custom-searchbar"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Users List */}
                    <div className="space-y-4">
                        {paginatedUsers.map((user) => (
                            <div key={user.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                                <div className="p-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div className="text-4xl">{user.avatar}</div>
                                            <div>
                                                <h3 className="text-lg font-bold text-gray-800">{user.fullName}</h3>
                                                <p className="text-sm text-gray-600">@{user.username}</p>
                                                {currentAdminRole === '1' && user.organizationId && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                                                        Org: {user.organizationId}
                                                    </span>
                                                )}
                                                <div className="flex items-center space-x-2 mt-1">
                                                    <IonIcon icon={mailOutline} className="text-xs text-gray-400" />
                                                    <span className="text-sm text-gray-500">{user.email}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-3">
                                            <div className="text-right space-y-1">
                                                <div className="flex items-center space-x-2">
                                                    <IonChip color={getStatusColor(user.status)} className="text-xs">
                                                        {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                                                    </IonChip>
                                                    <IonChip color={getRoleColor(user.role)} className="text-xs">
                                                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                                    </IonChip>
                                                </div>
                                                <div className="text-xs text-gray-500 flex items-center space-x-1">
                                                    <IonIcon icon={calendarOutline} />
                                                    <span>Joined {new Date(user.joinDate).toLocaleDateString()}</span>
                                                </div>
                                            </div>

                                            <div className="flex flex-col space-y-1">
                                                <IonButton
                                                    size="small"
                                                    fill="clear"
                                                    onClick={() => handleUserAction('view', user)}
                                                >
                                                    <IonIcon icon={eyeOutline} />
                                                </IonButton>
                                                <IonButton
                                                    size="small"
                                                    fill="clear"
                                                    onClick={() => handleUserAction('edit', user)}
                                                >
                                                    <IonIcon icon={createOutline} />
                                                </IonButton>
                                                {((currentAdminRole === '1' && user.id != 1 && user.id != currentUserId) || 
                                                  (currentAdminRole === '2' && user.organizationId == currentAdminOrgId && user.id != currentUserId)) && (
                                                    <IonButton
                                                        size="small"
                                                        fill="clear"
                                                        color="danger"
                                                        onClick={() => handleUserAction('delete', user)}
                                                    >
                                                        <IonIcon icon={trashOutline} />
                                                    </IonButton>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* User Stats */}
                                    <div className="mt-4 pt-4 border-t border-gray-100">
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div>
                                                <div className="text-lg font-bold text-blue-600">{user.pollsCreated}</div>
                                                <div className="text-xs text-gray-500">Polls Created</div>
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-green-600">{user.votesCount}</div>
                                                <div className="text-xs text-gray-500">Votes Cast</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500">Last Active</div>
                                                <div className="text-sm font-medium text-gray-700">
                                                    {new Date(user.lastActive).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-xl shadow-lg p-4 mt-6 gap-3">
                            <div className="text-sm text-gray-600 order-2 sm:order-1">
                                Showing {startIndex + 1} - {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
                            </div>
                            <div className="flex items-center space-x-2 order-1 sm:order-2">
                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronBackOutline} slot="icon-only" />
                                </IonButton>

                                {[...Array(totalPages)].map((_, i) => (
                                    <IonButton
                                        key={i + 1}
                                        fill={currentPage === i + 1 ? 'solid' : 'outline'}
                                        size="small"
                                        onClick={() => goToPage(i + 1)}
                                        style={{
                                            '--border-radius': '8px',
                                            minWidth: '36px',
                                            display: (totalPages <= 5 || i < 2 || i >= totalPages - 2 || Math.abs(i + 1 - currentPage) <= 1) ? 'inline-flex' : 'none'
                                        }}
                                    >
                                        {i + 1}
                                    </IonButton>
                                ))}

                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronForwardOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    )}

                    {filteredUsers.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">👥</div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">No Users Found</h3>
                            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
                        </div>
                    )}
                </div>

                {/* User Detail Modal */}
                <IonModal
                    ref={modal}
                    isOpen={isModalOpen}
                    onDidDismiss={() => setIsModalOpen(false)}
                    className="user-detail-modal"
                >
                    <IonHeader>
                        <IonToolbar className="modal-header-beautified">
                            <IonTitle className="modal-title-beautified">User Intelligence</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={() => setIsModalOpen(false)} color="dark">
                                    <IonIcon icon={closeOutline} slot="icon-only" />
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent>
                        {selectedUser && (
                            <div className="flex flex-col h-full bg-slate-50">
                                {/* Premium Profile Header */}
                                <div className="profile-header-beautified">
                                    <div className="avatar-large-beautified">
                                        {selectedUser.avatar}
                                    </div>
                                    <h2 className="profile-name-beautified">{selectedUser.fullName}</h2>
                                    <p className="profile-handle-beautified">@{selectedUser.username}</p>

                                    <div className="flex justify-center gap-2 mt-4">
                                        <IonChip color={getStatusColor(selectedUser.status)} outline>
                                            <IonIcon icon={selectedUser.status === 'active' ? checkmarkCircle : closeCircle} />
                                            <IonLabel>{selectedUser.status.toUpperCase()}</IonLabel>
                                        </IonChip>
                                        <IonChip color={getRoleColor(selectedUser.role)} outline>
                                            <IonIcon icon={shieldCheckmarkOutline} />
                                            <IonLabel>{selectedUser.role.toUpperCase()}</IonLabel>
                                        </IonChip>
                                    </div>
                                </div>

                                {/* Info Cards Grid */}
                                <div className="info-cards-container-beautified">
                                    {/* Account Info Card */}
                                    <div className="glass-card-beautified">
                                        <h3 className="card-title-beautified">
                                            <IonIcon icon={personOutline} color="success" />
                                            Account Identity
                                        </h3>
                                        <div className="space-y-4">
                                            <div className="info-item-beautified">
                                                <span className="info-label-beautified">Email Address</span>
                                                <span className="info-value-beautified">
                                                    <IonIcon icon={mailOutline} className="text-gray-400" />
                                                    {selectedUser.email}
                                                </span>
                                            </div>
                                            <div className="info-item-beautified">
                                                <span className="info-label-beautified">Official Role</span>
                                                <span className="info-value-beautified">
                                                    <IonIcon icon={shieldCheckmarkOutline} className="text-gray-400" />
                                                    {selectedUser.roleName || selectedUser.role}
                                                </span>
                                            </div>
                                            <div className="info-item-beautified">
                                                <span className="info-label-beautified">Registration Date</span>
                                                <span className="info-value-beautified">
                                                    <IonIcon icon={calendarOutline} className="text-gray-400" />
                                                    {new Date(selectedUser.joinDate).toLocaleDateString(undefined, { dateStyle: 'long' })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Activity Card */}
                                    <div className="glass-card-beautified">
                                        <h3 className="card-title-beautified">
                                            <IonIcon icon={eyeOutline} color="primary" />
                                            System Engagement
                                        </h3>
                                        <div className="space-y-4">
                                            <div className="info-item-beautified">
                                                <span className="info-label-beautified">Polls Published</span>
                                                <span className="info-value-beautified text-xl font-bold text-green-600">
                                                    {selectedUser.pollsCreated}
                                                </span>
                                            </div>
                                            <div className="info-item-beautified">
                                                <span className="info-label-beautified">Total Votes Cast</span>
                                                <span className="info-value-beautified text-xl font-bold text-blue-600">
                                                    {selectedUser.votesCount}
                                                </span>
                                            </div>
                                            <div className="info-item-beautified">
                                                <span className="info-label-beautified">Last Session</span>
                                                <span className="info-value-beautified">
                                                    <IonIcon icon={calendarOutline} className="text-gray-400" />
                                                    {new Date(selectedUser.lastActive).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Premium Footer Actions */}
                                <div className="modal-actions-beautified border-t mt-auto shadow-inner">
                                    <IonButton
                                        className="action-btn-beautified"
                                        fill="solid"
                                        color="success"
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            handleUserAction('edit', selectedUser);
                                        }}
                                    >
                                        <IonIcon icon={createOutline} slot="start" />
                                        PROFILE SETTINGS
                                    </IonButton>
                                    <IonButton
                                        className="action-btn-beautified"
                                        color={selectedUser.status === 'active' ? 'danger' : 'success'}
                                        fill="outline"
                                        onClick={() => {
                                            setIsModalOpen(false);
                                            handleUserAction(selectedUser.status === 'active' ? 'suspend' : 'activate', selectedUser);
                                        }}
                                    >
                                        <IonIcon icon={selectedUser.status === 'active' ? banOutline : checkmarkCircleOutline} slot="start" />
                                        {selectedUser.status === 'active' ? 'SUSPEND ACCESS' : 'RESTORE ACCESS'}
                                    </IonButton>
                                </div>
                            </div>
                        )}
                    </IonContent>
                </IonModal>

                {/* Edit User Modal */}
                <IonModal isOpen={isEditModalOpen} onDidDismiss={() => setIsEditModalOpen(false)}>
                    <IonHeader>
                        <IonToolbar color="primary">
                            <IonTitle>Edit User</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={() => setIsEditModalOpen(false)}>
                                    <IonIcon slot="icon-only" icon={closeOutline} />
                                </IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding">
                        {editingUser && (
                            <div>
                                <IonItem>
                                    <IonLabel position="stacked">First Name *</IonLabel>
                                    <IonInput
                                        value={editingUser.first_name}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, first_name: e.detail.value })}
                                        placeholder="Enter first name"
                                        required
                                    />
                                </IonItem>

                                <IonItem>
                                    <IonLabel position="stacked">Last Name *</IonLabel>
                                    <IonInput
                                        value={editingUser.last_name}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, last_name: e.detail.value })}
                                        placeholder="Enter last name"
                                        required
                                    />
                                </IonItem>

                                <IonItem>
                                    <IonLabel position="stacked">Email *</IonLabel>
                                    <IonInput
                                        type="email"
                                        value={editingUser.email}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, email: e.detail.value })}
                                        placeholder="Enter email"
                                        required
                                    />
                                </IonItem>

                                <IonItem>
                                    <IonLabel position="stacked">Phone</IonLabel>
                                    <IonInput
                                        type="tel"
                                        value={editingUser.phone}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, phone: e.detail.value })}
                                        placeholder="Enter phone number"
                                    />
                                </IonItem>

                                <IonItem>
                                    <IonLabel position="stacked">Role *</IonLabel>
                                    <IonSelect
                                        value={editingUser.role_id}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, role_id: e.detail.value })}
                                    >
                                        {roles.map(role => (
                                            <IonSelectOption key={role.id} value={role.id}>
                                                {role.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>

                                <IonItem>
                                    <IonLabel position="stacked">Organization</IonLabel>
                                    <IonSelect
                                        value={String(editingUser.organization_id || '')}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, organization_id: e.detail.value })}
                                        placeholder="Select organization"
                                    >
                                        <IonSelectOption value="">None</IonSelectOption>
                                        {organizations.map(org => (
                                            <IonSelectOption key={org.id} value={String(org.id)}>
                                                {org.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>

                                <IonItem>
                                    <IonLabel>Active Status</IonLabel>
                                    <IonToggle
                                        checked={editingUser.is_active}
                                        onIonChange={(e) => setEditingUser({ ...editingUser, is_active: e.detail.checked })}
                                    />
                                </IonItem>

                                <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                                    <IonButton expand="block" onClick={handleSaveEdit} disabled={saving}>
                                        <IonIcon slot="start" icon={saveOutline} />
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </IonButton>
                                    <IonButton expand="block" color="light" onClick={() => setIsEditModalOpen(false)}>
                                        <IonIcon slot="start" icon={closeOutline} />
                                        Cancel
                                    </IonButton>
                                </div>
                            </div>
                        )}
                    </IonContent>
                </IonModal>

                {/* Toast Notification */}
                <IonToast
                    isOpen={toast.show}
                    onDidDismiss={() => setToast({ ...toast, show: false })}
                    message={toast.message}
                    duration={3000}
                    color={toast.color}
                    position="top"
                    icon={toast.color === 'success' ? checkmarkCircle : closeCircle}
                    buttons={[
                        {
                            text: 'Dismiss',
                            role: 'cancel'
                        }
                    ]}
                />
            </IonContent>
        </IonPage>
    );
};

export default AdminUsers;
